"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  addDays,
  areIntervalsOverlapping,
  differenceInDays,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  parse,
  startOfWeek,
} from "date-fns";
import { AnimatePresence, motion } from "motion/react";
import { toZonedTime } from "date-fns-tz";
import { useTranslation } from "react-i18next";

import { type CalendarConfig, defaultConfig } from "../types/calendar-config";
import { TutorSlotComponent } from "../components/tutor/TutorSlotComponent.tsx";

import { useCalendarStore } from "@/features/calendar/store/useCalendarStore.tsx";
import { Button } from "@/components/ui/button.tsx";
import { useCalendarModalStore } from "@/features/calendar/store/useCalendarModalStore.tsx";
import { CalendarGridBase } from "@/features/calendar/components/shared/CalendarGridBase";
import { CalendarHourCell } from "@/features/calendar/components/shared/CalendarHourCell";
import { CalendarCurrentTimeIndicator } from "@/features/calendar/components/shared/CalendarCurrentTimeIndicator";
import GenericLoader from "@/components/GenericLoader.tsx";
import { CoachMarker } from "@/components/CoachMarker";

interface TutorCalendarViewProps {
  config?: Partial<CalendarConfig>;
}

export default function TutorCalendarView({ config = {} }: TutorCalendarViewProps) {
  const { t } = useTranslation(["help"]);
  const { shownDays, calendarData, setDateRange, dateRange, isLoading } = useCalendarStore();
  const [currentTime, setCurrentTime] = useState(new Date());
  const { setIsOpen, setDefaultDate, setDefaultTime, setAction } = useCalendarModalStore();
  const timeIndicatorRef = useRef<HTMLDivElement>(null);
  const finalConfig = useMemo(() => ({ ...defaultConfig, ...config }), [config]);

  const hours = useMemo(
    () =>
      Array.from(
        { length: finalConfig.maxHour - finalConfig.minHour },
        (_, i) => i + finalConfig.minHour,
      ),
    [finalConfig.maxHour, finalConfig.minHour],
  );

  const getWeekDays = () => {
    const days: Date[] = [];

    for (let i = 0; i < shownDays; i++) {
      const day = parse(dateRange.from, "yyyy-MM-dd", new Date());

      day.setDate(day.getDate() + i);
      days.push(day);
    }

    return days;
  };
  const weekDays = getWeekDays();

  useEffect(() => {
    setCurrentTime(toZonedTime(new Date(), calendarData?.displayTimezone ?? "UTC"));
    const timer = setInterval(
      () => setCurrentTime(toZonedTime(new Date(), calendarData?.displayTimezone ?? "UTC")),
      60000,
    );

    console.log(currentTime);

    return () => clearInterval(timer);
  }, [calendarData]);

  // Auto-scroll to time indicator on initial load
  useEffect(() => {
    timeIndicatorRef.current?.scrollIntoView({
      behavior: "instant",
      block: "center",
      inline: "center",
    });
  }, []);

  useEffect(() => {
    let newStartDate, newEndDate;

    const diff = differenceInDays(
      parse(dateRange.from, "yyyy-MM-dd", new Date()),
      parse(dateRange.to, "yyyy-MM-dd", new Date()),
    );

    if (diff < shownDays) {
      switch (shownDays) {
        case 7:
          newStartDate = format(
            startOfWeek(parse(dateRange.from, "yyyy-MM-dd", new Date()), { weekStartsOn: 1 }),
            "yyyy-MM-dd",
          );
          newEndDate = format(
            endOfWeek(parse(dateRange.from, "yyyy-MM-dd", new Date()), { weekStartsOn: 1 }),
            "yyyy-MM-dd",
          );
          break;
        case 3:
        case 1:
          newStartDate = format(parse(dateRange.from, "yyyy-MM-dd", new Date()), "yyyy-MM-dd");
          newEndDate = format(
            addDays(parse(dateRange.from, "yyyy-MM-dd", new Date()), shownDays - 1),
            "yyyy-MM-dd",
          );
          break;
      }
      if (
        calendarData &&
        isAfter(
          parse(newEndDate, "yyyy-MM-dd", new Date()),
          parse(calendarData.calendarEndsAt, "yyyy-MM-dd", new Date()),
        )
      ) {
        newEndDate = calendarData?.calendarEndsAt;
      }
      if (
        calendarData &&
        isBefore(
          parse(newStartDate, "yyyy-MM-dd", new Date()),
          parse(calendarData.calendarStartsAt, "yyyy-MM-dd", new Date()),
        )
      ) {
        newStartDate = calendarData?.calendarStartsAt;
      }
      if (
        newStartDate &&
        newEndDate &&
        (newStartDate !== dateRange.from || newEndDate !== dateRange.to)
      ) {
        setDateRange({
          from: newStartDate,
          to: newEndDate,
        });
      }
    }
  }, [shownDays]);

  const getEventsForDate = useCallback(
    (date: Date) => {
      let eventsForDate;

      if (calendarData && calendarData.data) {
        eventsForDate = calendarData.data.filter((item) => {
          return format(item.startTimeDateTimeString, "yyyy-MM-dd") === format(date, "yyyy-MM-dd");
        });

        return eventsForDate;
      }

      return [];
    },
    [calendarData],
  );

  return (
    <CalendarGridBase
      headerHeight={finalConfig.headerHeight}
      hourHeight={finalConfig.hourHeight}
      hours={hours}
      isLoading={isLoading}
      minHour={finalConfig.minHour}
      shownDays={shownDays}
      timeColumnWidth={finalConfig.timeColumnWidth}
      weekDays={weekDays}
    >
      {isLoading && <GenericLoader className={"fixed top-0 left-0 z-30"} />}
      {weekDays.slice(0, shownDays).map((day, dayIndex) => (
        <div key={`${day.toISOString()}`} className="relative">
          {hours.map((hour) => (
            <CalendarHourCell key={hour} hour={hour} isLoading={isLoading}>
              {isSameDay(day, currentTime) && currentTime.getHours() === hour && (
                <CalendarCurrentTimeIndicator
                  ref={timeIndicatorRef}
                  currentTime={currentTime}
                  hourHeight={finalConfig.hourHeight}
                />
              )}
              {!isLoading &&
                isAfter(
                  new Date(day).setHours(hour),
                  toZonedTime(new Date(), calendarData?.displayTimezone ?? "UTC"),
                ) &&
                toZonedTime(new Date(day), calendarData?.displayTimezone ?? "UTC").getTime() <=
                  toZonedTime(
                    new Date(new Date(day).setHours(hour, 0)),
                    calendarData?.displayTimezone ?? "UTC",
                  ).getTime() &&
                isBefore(
                  day,
                  parse(calendarData?.calendarEndsAt ?? "", "yyyy-MM-dd", new Date()).setHours(
                    23,
                    59,
                    59,
                  ),
                ) &&
                !getEventsForDate(day).find((event) =>
                  areIntervalsOverlapping(
                    {
                      start: parse(
                        event.startTimeDateTimeString,
                        "yyyy-MM-dd HH:mm:SS",
                        new Date(),
                      ),
                      end: parse(event.endTimeDateTimeString, "yyyy-MM-dd HH:mm:SS", new Date()),
                    },
                    {
                      start: toZonedTime(
                        new Date(day),
                        calendarData?.displayTimezone ?? "UTC",
                      ).setHours(hour, 0),
                      end: toZonedTime(
                        new Date(day),
                        calendarData?.displayTimezone ?? "UTC",
                      ).setHours(hour + 1, 0),
                    },
                  ),
                ) &&
                (isSameDay(day, currentTime) && currentTime.getHours() + 1 === hour ? (
                  <CoachMarker
                    className={`h-[${finalConfig.hourHeight}px]`}
                    description={t("help:coachMarkers.calendarGrid.description", {
                      defaultValue:
                        "Click on any empty time slot to create a new lesson at that specific time. The + button will appear when you hover over available slots.",
                    })}
                    id="calendar-grid"
                    placement="right"
                    title={t("help:coachMarkers.calendarGrid.title", {
                      defaultValue: "Calendar Grid - Click Empty Slots",
                    })}
                  >
                    <motion.div className={"h-full w-full p-1 opacity-0 hover:opacity-100"}>
                      <Button
                        className={"h-full w-full border-2 bg-transparent"}
                        onClick={() => {
                          setAction("create");
                          setDefaultDate(format(day, "yyyy-MM-dd"));
                          setDefaultTime(format(new Date().setHours(hour, 0), "HH:mm"));
                          setIsOpen(true);
                        }}
                      >
                        <div
                          className={"flex h-7 w-7 items-center justify-center rounded-md border-2"}
                        >
                          <span className={"iconify tabler--plus size-6"} />
                        </div>
                      </Button>
                    </motion.div>
                  </CoachMarker>
                ) : (
                  <motion.div className={"h-full w-full p-1 opacity-0 hover:opacity-100"}>
                    <Button
                      className={"h-full w-full border-2 bg-transparent"}
                      onClick={() => {
                        setAction("create");
                        setDefaultDate(format(day, "yyyy-MM-dd"));
                        setDefaultTime(format(new Date().setHours(hour, 0), "HH:mm"));
                        setIsOpen(true);
                      }}
                    >
                      <div
                        className={"flex h-7 w-7 items-center justify-center rounded-md border-2"}
                      >
                        <span className={"iconify tabler--plus size-6"} />
                      </div>
                    </Button>
                  </motion.div>
                ))}
            </CalendarHourCell>
          ))}
          <AnimatePresence initial={true} mode={"popLayout"}>
            {!isLoading &&
              getEventsForDate(day).map((event) => (
                <TutorSlotComponent
                  key={event.id}
                  dayIndex={dayIndex}
                  event={event}
                  finalConfig={finalConfig}
                  weekDaysLength={weekDays.length}
                />
              ))}
          </AnimatePresence>
        </div>
      ))}
    </CalendarGridBase>
  );
}
